<template>
  <div class="product-admin">
    <div class="toolbar">
      <div class="toolbar-left">
        <select v-model="filterCategoryId" class="filter-select" @change="loadList">
          <option value="">全部分类</option>
          <option v-for="cat in categories" :key="cat.id" :value="String(cat.id)">
            {{ cat.name_zh || cat.name_en }}
          </option>
        </select>
        <input
          v-model.trim="keyword"
          type="search"
          class="search-input"
          placeholder="搜索商品名称或介绍…"
          @keyup.enter="loadList"
        />
        <button type="button" class="btn btn-secondary" @click="loadList">搜索</button>
        <button v-if="keyword || filterCategoryId" type="button" class="btn btn-ghost" @click="resetSearch">
          重置
        </button>
      </div>
      <button type="button" class="btn btn-primary" @click="openCreate">+ 新增商品</button>
    </div>

    <p v-if="error" class="alert alert-error">{{ error }}</p>
    <p v-if="success" class="alert alert-success">{{ success }}</p>

    <div v-if="loading" class="loading">加载中…</div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th class="col-image">主图</th>
            <th>英文名称</th>
            <th>中文名称</th>
            <th>分类</th>
            <th class="col-desc">介绍</th>
            <th class="col-time">更新时间</th>
            <th class="col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td colspan="7" class="empty-cell">暂无商品，点击「新增商品」添加</td>
          </tr>
          <tr v-for="row in items" :key="row.id">
            <td class="col-image">
              <img
                v-if="productThumbUrl(row)"
                :src="productThumbUrl(row)"
                :alt="row.name_en"
                class="thumb"
              />
              <span v-else class="thumb-placeholder">无图</span>
            </td>
            <td>{{ row.name_en }}</td>
            <td>{{ row.name_zh || '—' }}</td>
            <td>{{ row.category_name_zh || row.category_name_en || '—' }}</td>
            <td class="col-desc">
              <span class="desc-text" :title="row.description">{{ row.description || '—' }}</span>
            </td>
            <td class="col-time">{{ formatTime(row.updated_at) }}</td>
            <td class="col-actions">
              <button type="button" class="btn-link" @click="openEdit(row)">编辑</button>
              <button type="button" class="btn-link danger" @click="confirmDelete(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="table-footer">共 {{ items.length }} 条 · 按更新时间倒序</p>
    </div>

    <div v-if="modalVisible" class="modal-mask" @click.self="closeModal">
      <div class="modal modal-wide">
        <h3 class="modal-title">{{ editingId ? '编辑商品' : '新增商品' }}</h3>
        <form class="modal-form" @submit.prevent="submitForm">
          <div class="form-row">
            <label>所属分类 <span class="req">*</span></label>
            <select v-model="form.category_id" class="input-lg" required>
              <option disabled value="">请选择分类</option>
              <option v-for="cat in categories" :key="cat.id" :value="String(cat.id)">
                {{ cat.name_zh || cat.name_en }}（{{ cat.name_en }}）
              </option>
            </select>
          </div>

          <div class="form-row form-row-grid">
            <div>
              <label>英文名称 <span class="req">*</span></label>
              <input v-model.trim="form.name_en" type="text" class="input-lg" required />
            </div>
            <div>
              <label>中文名称</label>
              <input v-model.trim="form.name_zh" type="text" class="input-lg" />
            </div>
          </div>

          <div class="form-row">
            <label>主图 <span class="req">*</span></label>
            <div class="image-upload">
              <div v-if="mainPreview" class="image-preview-wrap">
                <img :src="mainPreview" alt="主图预览" class="image-preview" />
                <button type="button" class="btn-link danger" @click="removeMainImage">移除主图</button>
              </div>
              <p v-else class="image-hint">支持 JPG、PNG、GIF、WEBP，最大 5MB</p>
              <input
                ref="mainImageInput"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                class="file-input"
                @change="onMainImageSelected"
              />
            </div>
          </div>

          <div class="form-row">
            <label>详细图</label>
            <p class="image-hint">可上传多张，保存后存入 uploads/product</p>
            <div v-if="detailPreviews.length" class="detail-grid">
              <div v-for="(item, index) in detailPreviews" :key="item.key" class="detail-item">
                <img :src="item.url" alt="详细图" class="detail-thumb" />
                <button type="button" class="btn-link danger detail-remove" @click="removeDetailImage(index)">
                  移除
                </button>
              </div>
            </div>
            <input
              ref="detailImageInput"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/gif,image/webp"
              class="file-input"
              @change="onDetailImagesSelected"
            />
          </div>

          <div class="form-row">
            <label>介绍 <span class="req">*</span></label>
            <textarea
              v-model.trim="form.description"
              class="textarea-lg"
              rows="8"
              placeholder="商品介绍"
              required
            />
          </div>

          <p v-if="formError" class="alert alert-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeModal">取消</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? '保存中…' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchCategories } from '@/api/categories'
import {
  createProduct,
  deleteProduct,
  fetchProducts,
  resolveUploadUrl,
  updateProduct,
  uploadProductImage
} from '@/api/products'

const emptyForm = () => ({
  category_id: '',
  name_en: '',
  name_zh: '',
  description: '',
  main_image_path: ''
})

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
const MAX_BYTES = 5 * 1024 * 1024

export default {
  name: 'AdminProducts',
  data() {
    return {
      items: [],
      categories: [],
      keyword: '',
      filterCategoryId: '',
      loading: false,
      saving: false,
      error: '',
      success: '',
      modalVisible: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      pendingMainFile: null,
      mainPreview: '',
      mainObjectUrl: '',
      detailPreviews: []
    }
  },
  created() {
    this.loadCategories()
    this.loadList()
  },
  beforeUnmount() {
    this.revokeMainObjectUrl()
    this.revokeDetailObjectUrls()
  },
  methods: {
    productThumbUrl(row) {
      if (!row) return ''
      return resolveUploadUrl(row.main_image_url || row.main_image_path || '')
    },
    formatTime(value) {
      if (!value) return '—'
      const d = new Date(value.replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    validateImageFile(file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        return '仅支持 JPG、PNG、GIF、WEBP 图片'
      }
      if (file.size > MAX_BYTES) {
        return '图片不能超过 5MB'
      }
      return ''
    },
    async loadCategories() {
      try {
        const res = await fetchCategories()
        this.categories = res.data?.items || []
      } catch {
        this.categories = []
      }
    },
    async loadList() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchProducts({
          keyword: this.keyword,
          categoryId: this.filterCategoryId
        })
        this.items = res.data?.items || []
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      this.keyword = ''
      this.filterCategoryId = ''
      this.loadList()
    },
    revokeMainObjectUrl() {
      if (this.mainObjectUrl) {
        URL.revokeObjectURL(this.mainObjectUrl)
        this.mainObjectUrl = ''
      }
    },
    revokeDetailObjectUrls() {
      this.detailPreviews.forEach((item) => {
        if (item.objectUrl) URL.revokeObjectURL(item.objectUrl)
      })
    },
    resetImageState() {
      this.pendingMainFile = null
      this.revokeMainObjectUrl()
      this.mainPreview = ''
      if (this.$refs.mainImageInput) this.$refs.mainImageInput.value = ''
      this.revokeDetailObjectUrls()
      this.detailPreviews = []
      if (this.$refs.detailImageInput) this.$refs.detailImageInput.value = ''
    },
    onMainImageSelected(event) {
      const file = event.target.files?.[0]
      if (!file) return
      const err = this.validateImageFile(file)
      if (err) {
        this.formError = err
        event.target.value = ''
        return
      }
      this.formError = ''
      this.pendingMainFile = file
      this.revokeMainObjectUrl()
      this.mainObjectUrl = URL.createObjectURL(file)
      this.mainPreview = this.mainObjectUrl
    },
    removeMainImage() {
      this.form.main_image_path = ''
      this.pendingMainFile = null
      this.revokeMainObjectUrl()
      this.mainPreview = ''
      if (this.$refs.mainImageInput) this.$refs.mainImageInput.value = ''
    },
    onDetailImagesSelected(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      for (const file of files) {
        const err = this.validateImageFile(file)
        if (err) {
          this.formError = err
          continue
        }
        const objectUrl = URL.createObjectURL(file)
        this.detailPreviews.push({
          key: `new-${Date.now()}-${Math.random()}`,
          url: objectUrl,
          objectUrl,
          path: null,
          file,
          isNew: true
        })
      }
    },
    removeDetailImage(index) {
      const item = this.detailPreviews[index]
      if (item?.objectUrl) URL.revokeObjectURL(item.objectUrl)
      this.detailPreviews.splice(index, 1)
    },
    openCreate() {
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.resetImageState()
      this.modalVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.form = {
        category_id: String(row.category_id),
        name_en: row.name_en,
        name_zh: row.name_zh || '',
        description: row.description || '',
        main_image_path: row.main_image_path || ''
      }
      this.formError = ''
      this.resetImageState()
      this.mainPreview = this.productThumbUrl(row)
      this.detailPreviews = (row.detail_images || []).map((path, i) => ({
        key: `existing-${path}-${i}`,
        url: resolveUploadUrl(`/uploads/${path}`),
        objectUrl: '',
        path,
        isNew: false
      }))
      this.modalVisible = true
    },
    closeModal() {
      this.modalVisible = false
      this.formError = ''
      this.resetImageState()
    },
    async uploadPendingImages() {
      let mainPath = this.form.main_image_path || ''
      if (this.pendingMainFile) {
        const res = await uploadProductImage(this.pendingMainFile)
        mainPath = res.data?.image_path || ''
      }

      const detailPaths = []
      for (const item of this.detailPreviews) {
        if (item.path && !item.isNew) {
          detailPaths.push(item.path)
        } else if (item.file) {
          const res = await uploadProductImage(item.file)
          if (res.data?.image_path) detailPaths.push(res.data.image_path)
        }
      }

      return { mainPath, detailPaths }
    },
    async submitForm() {
      this.formError = ''
      if (!this.form.category_id) {
        this.formError = '请选择分类'
        return
      }
      if (!this.form.name_en || !this.form.description) {
        this.formError = '请填写英文名称和介绍'
        return
      }
      if (!this.pendingMainFile && !this.form.main_image_path) {
        this.formError = '请上传主图'
        return
      }

      this.saving = true
      try {
        const { mainPath, detailPaths } = await this.uploadPendingImages()
        const payload = {
          category_id: Number(this.form.category_id),
          name_en: this.form.name_en,
          name_zh: this.form.name_zh,
          description: this.form.description,
          main_image_path: mainPath,
          detail_images: detailPaths
        }

        if (this.editingId) {
          await updateProduct(this.editingId, payload)
          this.success = '商品已更新'
        } else {
          await createProduct(payload)
          this.success = '商品已创建'
        }
        this.closeModal()
        await this.loadList()
        await this.loadCategories()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.formError = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      const name = row.name_zh || row.name_en
      if (!window.confirm(`确定删除商品「${name}」？`)) return
      this.doDelete(row.id)
    },
    async doDelete(id) {
      this.error = ''
      try {
        await deleteProduct(id)
        this.success = '已删除'
        await this.loadList()
        await this.loadCategories()
        setTimeout(() => {
          this.success = ''
        }, 2500)
      } catch (e) {
        this.error = e.message || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.product-admin {
  max-width: 1280px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-select,
.search-input {
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.filter-select {
  min-width: 160px;
}

.search-input {
  width: 240px;
}

.btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: #0d9488;
  color: #fff;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #fff;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.btn-ghost {
  background: transparent;
  color: #64748b;
}

.alert {
  margin: 0 0 12px;
  padding: 10px 14px;
  font-size: 13px;
  border-radius: 8px;
}

.alert-error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.alert-success {
  color: #047857;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.data-table th {
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  font-size: 13px;
}

.data-table tbody tr:hover {
  background: #fafbfc;
}

.col-image {
  width: 88px;
}

.thumb {
  display: block;
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.thumb-placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  border-radius: 8px;
}

.col-desc {
  max-width: 280px;
}

.desc-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
  color: #475569;
}

.col-time {
  width: 150px;
  white-space: nowrap;
  color: #64748b;
  font-size: 13px;
}

.col-actions {
  width: 100px;
  white-space: nowrap;
}

.btn-link {
  margin-right: 10px;
  padding: 0;
  font-size: 13px;
  color: #0d9488;
  background: none;
  border: none;
  cursor: pointer;
}

.btn-link.danger {
  color: #dc2626;
}

.empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 32px !important;
}

.table-footer {
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  color: #94a3b8;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
}

.modal {
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 28px 32px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.15);
}

.modal-wide {
  max-width: 840px;
}

.modal-title {
  margin: 0 0 20px;
  font-size: 18px;
  color: #0f172a;
}

.modal-form label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.req {
  color: #dc2626;
}

.modal-form input,
.modal-form textarea,
.modal-form select {
  width: 100%;
  box-sizing: border-box;
  padding: 9px 12px;
  font-size: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.modal-form .input-lg {
  padding: 12px 14px;
  font-size: 15px;
}

.modal-form .textarea-lg {
  min-height: 160px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.6;
  resize: vertical;
}

.form-row {
  margin-bottom: 16px;
}

.form-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .form-row-grid {
    grid-template-columns: 1fr;
  }
}

.image-upload {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.image-preview-wrap {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.image-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: #64748b;
}

.file-input {
  font-size: 14px;
}

.detail-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}

.detail-item {
  position: relative;
}

.detail-thumb {
  display: block;
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.detail-remove {
  display: block;
  margin-top: 4px;
  font-size: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
